import * as React from 'react';
import { ICustomerPricingLookupProps } from './CustomerPricingLookup.properties';
import { getCustomerPricingLookupColumns, SEARCH_SCREEN_DIALOG_STYLE } from './constants';
import PriceCalculator from './PriceCalculator';
import BasicLookupActionField from 'components/FormView/Fields/BasicLookupActionField';
import { useCustomerPriceCodeCalculator } from 'api/CustomerPriceCode/customerPriceCode';
import { useCPLContextSelector, useCPLDispatch } from './CustomerPricingLookup.context';
import { CustomerPricingLookupActionTypes } from './CustomerPricingLookup.actions';
import { initialState } from './CustomerPricingLookup.reducer';
import { IPriceCalculatorHandle } from './PriceCalculator/PriceCalculator.properties';
import { IBasicLookupFieldHandle } from '@markinson/uicomponents-v2/BasicLookupField/BasicLookup.properties';

const CustomerPricingLookup = (props: Readonly<ICustomerPricingLookupProps>, ref: React.Ref<IBasicLookupFieldHandle>) => {
    const { ProductId, Warehouse, Description, onSelectedItemChange, onChange, value, fieldRef, TaxIncluded = false } = props;
    const useCustomerPriceCalculatorMutation = useCustomerPriceCodeCalculator();
    const contextDispatch = useCPLDispatch();
    const calculatedValues = useCPLContextSelector<'CalculatedData'>((state) => state.CalculatedData);
    const [initialPriceValue, setInitialPriceValue] = React.useState<number>();
    const calculatorRef = React.useRef<IPriceCalculatorHandle>(null);

    React.useEffect(
        () => {
            setValueField(props?.value);
        },
        [props?.value]);

    const setValueField = (val) => {
        if (val !== undefined && !isNaN(Number(val))) {
            setInitialPriceValue(Number(val));
        }
    };

    const onSelectedItemChanged = (item, inputText) => {
        if (onChange) {
            onChange(item?.ReturnValue ?? inputText);
        }
        if (onSelectedItemChange) {
            onSelectedItemChange(item ? { ...item, ReturnValue: item?.ReturnValue ?? inputText } : { ReturnValue: inputText, Details: { Discount: '0' } }, inputText);
        }
    };

    const calculateCustomerPrice = async (initialize: boolean = true) => {
        const response = await useCustomerPriceCalculatorMutation.mutateAsync({
            ...calculatedValues,
            Cost: calculatedValues?.Cost ?? 0,
            TaxRate: calculatedValues?.TaxRate ?? 0,
            GrossProfit: calculatedValues?.GrossProfit ?? 0,
            SellPrice: initialize ? initialPriceValue : calculatedValues?.SellPrice,
            TaxIncludedPrice: initialize ? TaxIncluded : calculatedValues?.TaxIncludedPrice,
            WarehouseEntity: Warehouse,
            ProductId: Number(ProductId),
            urlQueryParams: {
                Initialise: initialize
            }
        });

        if (response?.Status && response?.CustomerPricingCalculator && !response?.Forms) {
            contextDispatch({
                [CustomerPricingLookupActionTypes.setInitialCalculatordData]: response?.CustomerPricingCalculator?.inlineObject,
                [CustomerPricingLookupActionTypes.setCalculatedData]: response?.CustomerPricingCalculator?.inlineObject
            });
        }
    };

    const onCloseSearchScreen = (item: any, additionalValues?: any) => {
        if (additionalValues && additionalValues?.action === 'OK' && additionalValues?.activeModule === 'Calculator' && onSelectedItemChange) {
            onSelectedItemChanged({ ...item, ReturnValue: calculatedValues?.SellPrice, Details: { ...item?.Details, Discount: 0 } }, '');
        }
        contextDispatch({
            [CustomerPricingLookupActionTypes.setInitialCalculatordData]: initialState?.InitialCalculatorData,
            [CustomerPricingLookupActionTypes.setCalculatedData]: initialState?.CalculatedData
        });
    };

    const onModuleChange = (moduleId: string) => {
        if (moduleId === 'Calculator' && Warehouse && ProductId && !calculatedValues) {
            calculateCustomerPrice().then(() => calculatorRef?.current?.focusFirstField());
        }
    };

    const additionalTabs = [
        { id: 'Calculator', label: 'Calculator', content: sampleDialogContent(), focusIn: () => calculatorRef?.current?.focusFirstField() }
    ];

    function sampleDialogContent(): JSX.Element {
        return <PriceCalculator
            innerRef={calculatorRef}
            onCalculate={() => calculateCustomerPrice(false)}
            onClose={() => undefined
            }
        />;
    }

    return (

        <BasicLookupActionField
            {...props}
            ref={fieldRef ? fieldRef : ref}
            params={{ ...props?.params, Warehouse: props?.Warehouse, ProductId: Number(props?.ProductId), KeyId: props?.KeyId?.toString(), TableId: props.TableId, Quantity: props?.Quantity ?? 0 }}
            displayField={'ReturnValue'}
            label={'Price'}
            suppressValidation={true}
            showSummaryPanel={false}
            lookupName={'CustomerPricing'}
            lookupOverrides={
                {
                    columns: getCustomerPricingLookupColumns(),
                    dialogStyle: SEARCH_SCREEN_DIALOG_STYLE,
                    hideColumnHeaders: false,
                    disableGridColumnLines: false
                }
            }
            onSelectedItemChange={onSelectedItemChanged}
            column1Field={'FormattedValue'}
            column2Field={'Description'}
            isTabbedLookup={true}
            searchScreenTitle={'Customer Pricing Lookup'}
            shouldEnterBehaveAsTab={true}
            additionalTabs={additionalTabs}
            shouldDefaultSearchText={false}
            onModuleChange={onModuleChange}
            onValueChange={setValueField}
            onCloseSearchScreen={onCloseSearchScreen}
            suppressDescription={true}
            description={Description}
            alignment={'right'}
            textFieldType={'number'}
            value={Number(value || 0).toFixed(2)?.toString()}
            byPassInternalDescription={true}
            disableOptionsLookup={true}
        />);
};

export default React.forwardRef(CustomerPricingLookup);
